import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { addDays, startOfMonth, endOfMonth } from 'date-fns';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

const EVENTS_COLLECTION = 'events';

// Get events for the current user
export const getEventsByUser = async (userId) => {
  try {
    // One query per way an event can be visible to this user (firestore.rules only lets a
    // client read public events and ones it attends, created or is grouped in).
    const eventsRef = collection(db, EVENTS_COLLECTION);
    const snapshots = await Promise.all([
      getDocs(query(eventsRef, where('visibility', '==', 'public'))),
      getDocs(query(eventsRef, where('attendeeUserIds', 'array-contains', userId))),
      getDocs(query(eventsRef, where('createdBy', '==', userId))),
      getDocs(query(eventsRef, where('visibility', '==', 'group'), where('groupId', '==', userId))),
    ]);
    const byId = new Map();
    snapshots.forEach((snapshot) => snapshot.docs.forEach((d) => byId.set(d.id, d)));
    const startMillis = (d) => d.data().startTime?.toMillis?.() ?? 0;
    const allEvents = [...byId.values()].sort((a, b) => startMillis(a) - startMillis(b)).map(doc => {
      const eventData = doc.data();
      return {
        id: doc.id,
        title: eventData.title,
        description: eventData.description,
        start: eventData.startTime?.toDate() || null,
        end: eventData.endTime?.toDate() || null,
        startTime: eventData.startTime?.toDate() || null,
        endTime: eventData.endTime?.toDate() || null,
        visibility: eventData.visibility || 'public',
        location: eventData.location || '',
        attendees: eventData.attendees || [],
        attendeeUserIds: eventData.attendeeUserIds || [],
        createdBy: eventData.createdBy,
        ...eventData
      };
    });

    // Filter events for the current user
    const userEvents = allEvents.filter(event => {
      return (
        event.visibility === "public" ||
        event.attendeeUserIds?.includes(userId) ||
        event.createdBy === userId ||
        (event.visibility === "group" && event.groupId === userId)
      );
    });

    return userEvents;
  } catch (error) {
    console.error('Error getting events by user:', error);
    throw error;
  }
};

// Get events for a specific timeframe
export const getEventsByTimeframe = async (userId, timeframe) => {
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const endOfWeek = new Date(today);
    endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    let startDate, endDate;

    switch (timeframe) {
      case 'Today':
        startDate = today;
        endDate = tomorrow;
        break;
      case 'This Week':
        startDate = today;
        endDate = endOfWeek;
        break;
      case 'This Month':
        startDate = today;
        endDate = endOfMonth;
        break;
      default:
        startDate = today;
        endDate = tomorrow;
    }

    const eventsRef = collection(db, EVENTS_COLLECTION);
    const q = query(
      eventsRef,
      where('startTime', '>=', startDate),
      where('startTime', '<=', endDate),
      orderBy('startTime', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const fetchedEvents = querySnapshot.docs.map(doc => {
      const eventData = doc.data();
      return {
        id: doc.id,
        title: eventData.title,
        description: eventData.description,
        start: eventData.startTime?.toDate() || null,
        end: eventData.endTime?.toDate() || null,
        startTime: eventData.startTime?.toDate() || null,
        endTime: eventData.endTime?.toDate() || null,
        visibility: eventData.visibility || 'public',
        location: eventData.location || '',
        attendees: eventData.attendees || [],
        attendeeUserIds: eventData.attendeeUserIds || [],
        createdBy: eventData.createdBy,
        ...eventData
      };
    });

    // Filter events for the current user
    const userEvents = fetchedEvents.filter(event => {
      return (
        event.visibility === "public" ||
        event.attendeeUserIds?.includes(userId) ||
        event.createdBy === userId ||
        (event.visibility === "group" && event.groupId === userId)
      );
    });

    return userEvents;
  } catch (error) {
    console.error('Error getting events by timeframe:', error);
    throw error;
  }
};

// Get upcoming events (next 30 days)
export const getUpcomingEvents = async (userId) => {
  try {
    const now = new Date();
    const next30Days = addDays(now, 30);

    const eventsRef = collection(db, EVENTS_COLLECTION);
    const q = query(
      eventsRef,
      where('startTime', '>=', now),
      where('startTime', '<=', next30Days),
      orderBy('startTime', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const fetchedEvents = querySnapshot.docs.map(doc => {
      const eventData = doc.data();
      return {
        id: doc.id,
        title: eventData.title,
        description: eventData.description,
        start: eventData.startTime?.toDate() || null,
        end: eventData.endTime?.toDate() || null,
        startTime: eventData.startTime?.toDate() || null,
        endTime: eventData.endTime?.toDate() || null,
        visibility: eventData.visibility || 'public',
        location: eventData.location || '',
        attendees: eventData.attendees || [],
        attendeeUserIds: eventData.attendeeUserIds || [],
        createdBy: eventData.createdBy,
        ...eventData
      };
    });

    // Filter events for the current user
    const userEvents = fetchedEvents.filter(event => {
      return (
        event.visibility === "public" ||
        event.attendeeUserIds?.includes(userId) ||
        event.createdBy === userId ||
        (event.visibility === "group" && event.groupId === userId)
      );
    });

    return userEvents;
  } catch (error) {
    console.error('Error getting upcoming events:', error);
    throw error;
  }
};

// Get events by actual account ID (for CRM view) - events created by users from this account
export const getEventsByActualAccountId = async (accountId) => {
  try {
    // First, get all users associated with this account
    const usersRef = collection(db, 'users');
    const usersQuery = query(usersRef, where('accountId', '==', accountId));
    const usersSnapshot = await getDocs(usersQuery);
    
    const userIds = usersSnapshot.docs.map(doc => doc.id);
    
    if (userIds.length === 0) {
      return [];
    }
    
    // Query for events where any of these users are the createdBy
    const eventsRef = collection(db, EVENTS_COLLECTION);
    const eventsPromises = userIds.map(userId => {
      const q = query(
        eventsRef,
        where('createdBy', '==', userId)
      );
      return getDocs(q);
    });
    
    const eventsSnapshots = await Promise.all(eventsPromises);
    
    // Combine and deduplicate events
    const allEvents = [];
    eventsSnapshots.forEach(snapshot => {
      const events = snapshot.docs.map(doc => {
        const eventData = doc.data();
        return {
          id: doc.id,
          title: eventData.title,
          description: eventData.description,
          start: eventData.startTime?.toDate() || null,
          end: eventData.endTime?.toDate() || null,
          startTime: eventData.startTime?.toDate() || null,
          endTime: eventData.endTime?.toDate() || null,
          visibility: eventData.visibility || 'public',
          location: eventData.location || '',
          attendees: eventData.attendees || [],
          attendeeUserIds: eventData.attendeeUserIds || [],
          createdBy: eventData.createdBy,
          ...eventData
        };
      });
      allEvents.push(...events);
    });
    
    // Remove duplicates based on event ID
    const uniqueEvents = allEvents.filter((event, index, self) => 
      index === self.findIndex(e => e.id === event.id)
    );
    
    // Sort by start time
    return uniqueEvents.sort((a, b) => {
      if (!a.startTime && !b.startTime) return 0;
      if (!a.startTime) return 1;
      if (!b.startTime) return -1;
      return a.startTime - b.startTime; // Ascending order (earliest first)
    });
    
  } catch (error) {
    console.error('Error getting events by actual account ID:', error);
    throw error;
  }
}; 